@if (isRich()) {
	<div class="field-wrapper rich">
		@if (!hideLabel()) {
			<span class="field-label" [id]="labelId">
				{{ label() }}
				@if (required()) {
					<span class="required-asterisk">&nbsp;*</span>
				}
			</span>
		}

		@let message = errorMessage;
		<div class="editor-frame" [class.invalid]="message !== null" [class.disabled]="disabled()" (focusin)="onFocusIn()" (focusout)="onFocusOut($event)">
			@if (!disabled()) {
				<div #toolbar class="rich-toolbar dense-3" role="toolbar" [attr.aria-label]="label() + ' formatting'" (keydown)="onToolbarKeydown($event)">
					@for (item of toolbarItems(); track $index) {
						@if (item.id === 'separator') {
							<span class="sep" role="separator"></span>
						} @else {
							<button
								mat-icon-button
								type="button"
								[matTooltip]="item.label"
								[attr.aria-label]="item.label"
								[attr.aria-pressed]="item.toggle ? isActive(item) : null"
								[attr.aria-expanded]="item.id === 'link' || item.id === 'symbol' ? openPanel() === item.id : null"
								[class.active]="item.toggle && isActive(item)"
								[tabIndex]="toolbarFocusIndex() === item.buttonIndex ? 0 : -1"
								(focus)="toolbarFocusIndex.set(item.buttonIndex)"
								(mousedown)="$event.preventDefault()"
								(click)="run(item)"
							>
								<mat-icon>{{ item.icon }}</mat-icon>
							</button>
						}
					}
				</div>

				@if (openPanel() === 'link') {
					<div class="tool-panel link-popover" role="group" aria-label="Link details" (keydown.escape)="$event.stopPropagation(); cancelPanel()">
						<label class="link-field">
							<span class="link-label">Text</span>
							<input
								#linkTextInput
								class="link-input"
								type="text"
								placeholder="Text to display"
								[value]="linkText()"
								(input)="onLinkTextInput($any($event.target).value)"
								(keydown.enter)="$event.preventDefault(); applyLink()"
							/>
						</label>
						<label class="link-field">
							<span class="link-label">Link</span>
							<input
								#linkInput
								class="link-input"
								type="url"
								placeholder="https://"
								[value]="linkDraft()"
								[attr.aria-invalid]="linkError() ? 'true' : null"
								(input)="onLinkDraftInput($any($event.target).value)"
								(keydown.enter)="$event.preventDefault(); applyLink()"
							/>
						</label>
						<div class="link-actions">
							<button mat-button type="button" class="link-apply" (mousedown)="$event.preventDefault()" (click)="applyLink()">
								{{ editedAnchor() ? 'Update' : 'Apply' }}
							</button>
							@if (editedAnchor()) {
								<button mat-button type="button" class="link-remove" (mousedown)="$event.preventDefault()" (click)="removeLink()">
									Remove
								</button>
							}
							<button mat-button type="button" class="link-cancel" (mousedown)="$event.preventDefault()" (click)="cancelPanel()">Cancel</button>
						</div>
						@if (linkError(); as error) {
							<span class="link-error" role="alert">{{ error }}</span>
						}
					</div>
				}

				@if (openPanel() === 'symbol') {
					<div class="tool-panel symbol-panel" role="group" aria-label="Insert symbol" (keydown.escape)="$event.stopPropagation(); cancelPanel()">
						@for (symbol of symbols; track symbol.char) {
							<button
								type="button"
								class="symbol"
								[matTooltip]="symbol.name"
								[attr.aria-label]="'Insert ' + symbol.name"
								(mousedown)="$event.preventDefault()"
								(click)="insertSymbol(symbol)"
							>
								{{ symbol.char }}
							</button>
						}
					</div>
				}
			}

			<div class="surface-wrap">
				<div
					#surface
					class="rich-editor nha-authored-content"
					role="textbox"
					aria-multiline="true"
					[attr.aria-label]="hideLabel() ? label() : null"
					[attr.aria-labelledby]="hideLabel() ? null : labelId"
					[attr.aria-describedby]="hint() || message !== null ? subscriptId : null"
					[attr.aria-placeholder]="placeholder() || null"
					[attr.aria-required]="required() ? 'true' : null"
					[attr.aria-invalid]="message !== null ? 'true' : null"
					[attr.aria-readonly]="disabled() ? 'true' : null"
					[attr.contenteditable]="disabled() ? null : 'true'"
					(input)="onRichInput()"
					(paste)="onPaste($event)"
				></div>
				@if (isBlank() && placeholder()) {
					<div class="placeholder" aria-hidden="true">{{ placeholder() }}</div>
				}
			</div>
		</div>

		@if (message !== null) {
			<div class="subscript error" [id]="subscriptId">{{ message }}</div>
		} @else if (hint()) {
			<div class="subscript hint" [id]="subscriptId">{{ hint() }}</div>
		}
	</div>
} @else {
	<div class="field-wrapper">
		<mat-form-field [appearance]="appearance()" [floatLabel]="floatLabelAlways() ? 'always' : 'auto'" subscriptSizing="dynamic" [hideRequiredMarker]="true">
			@if (!hideLabel()) {
				<mat-label
					>{{ label() }}
					@if (required()) {
						<span class="required-asterisk">&nbsp;*</span>
					}
				</mat-label>
			}
			<!-- The autosize directive's host class also removes the resize handle, so it is only attached when opted in. -->
			@if (autoGrow()) {
				<textarea
					matInput
					cdkTextareaAutosize
					[cdkAutosizeMinRows]="minRows()"
					[cdkAutosizeMaxRows]="maxRows()"
					[errorStateMatcher]="errorStateMatcher"
					[attr.aria-label]="hideLabel() ? label() : null"
					[attr.maxlength]="maxLength()"
					[placeholder]="placeholder()"
					[disabled]="disabled()"
					[required]="required()"
					[value]="value() ?? ''"
					(input)="onInput($any($event.target).value)"
					(blur)="onBlur()"
				></textarea>
			} @else {
				<textarea
					matInput
					[errorStateMatcher]="errorStateMatcher"
					[attr.aria-label]="hideLabel() ? label() : null"
					[rows]="rows()"
					[attr.maxlength]="maxLength()"
					[placeholder]="placeholder()"
					[disabled]="disabled()"
					[required]="required()"
					[value]="value() ?? ''"
					(input)="onInput($any($event.target).value)"
					(blur)="onBlur()"
				></textarea>
			}
			@if (errorMessage; as msg) {
				<mat-error>{{ msg }}</mat-error>
			} @else if (hint()) {
				<mat-hint>{{ hint() }}</mat-hint>
			}
		</mat-form-field>
	</div>
}
